/**
 * useChatRealtimeSync.ts
 * Propaga atribuição/meta/CRM/favoritos cross-user via PartyKit.
 */

import { useEffect, useRef, useCallback } from 'react';
import { createModuleLogger } from '../../../utils/logger';
import { usePartykitSync } from './usePartykitSync';
import { PARTYKIT_LIST_REFRESH_MS, postPartykitOrgEvent } from '../utils/partykitSocket';

const log = createModuleLogger('[ChatRealtime]');
const REFRESH_DEBOUNCE_MS = PARTYKIT_LIST_REFRESH_MS;

type MetaDetail = {
    phone?: string;
    remoteJid?: string;
    connectionId?: string;
    userId?: string | null;
    organizationId?: string;
    status?: 'closed' | 'active';
    unreadCount?: number;
    source?: string;
};

type FavoriteDetail = {
    conversationId?: string;
    userId?: string;
    action?: 'INSERT' | 'DELETE';
    organizationId?: string;
    source?: string;
};

function broadcastMeta(detail: MetaDetail, broadcastConversationChange: (
    id?: string,
    assignment?: { phone?: string; assignedTo?: string | null; organizationId?: string; status?: string; unreadCount?: number },
) => void) {
    if (!detail.organizationId) return;
    if (detail.source === 'partykit' || detail.source === 'broadcast') return;

    // Não enviar assignedTo em eventos só de status/unread — senão zera a atribuição no cliente.
    const hasAssignment = Object.prototype.hasOwnProperty.call(detail, 'userId');
    const payload: {
        phone?: string;
        assignedTo?: string | null;
        organizationId: string;
        status?: string;
        unreadCount?: number;
        connectionId?: string;
        remoteJid?: string;
    } = {
        phone: detail.phone,
        organizationId: detail.organizationId,
        status: detail.status,
        unreadCount: detail.unreadCount,
        connectionId: detail.connectionId,
        remoteJid: detail.remoteJid,
        ...(hasAssignment ? { assignedTo: detail.userId ?? null } : {}),
    };
    broadcastConversationChange(detail.phone ?? detail.remoteJid, payload);
    void postPartykitOrgEvent(detail.organizationId, 'status', {
        type: 'CONVERSATION_CHANGED',
        ...payload,
    });
}

export function useChatRealtimeSync(onRefreshNeeded: () => void, enabled = true) {
    const refreshRef = useRef(onRefreshNeeded);
    refreshRef.current = onRefreshNeeded;
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const scheduleRefresh = useCallback(() => {
        // Uma janela fixa impede que tráfego contínuo adie indefinidamente o refresh.
        if (timerRef.current) return;
        timerRef.current = setTimeout(() => {
            log.log('🔔 Atualizando lista (debounced)');
            refreshRef.current();
            timerRef.current = null;
        }, REFRESH_DEBOUNCE_MS);
    }, []);

    const {
        broadcastConversationChange,
        broadcastOpportunityChange,
        broadcastFavoriteChange,
    } = usePartykitSync({
        enabled,
        onOpportunityChange: scheduleRefresh,
        onContactChange: scheduleRefresh,
        onConversationChange: scheduleRefresh,
    });

    useEffect(() => {
        if (!enabled) return;

        const handleNewMessage = () => scheduleRefresh();
        const handleStageChanged = (event: Event) => {
            const detail = (event as CustomEvent<{ leadId?: string }>).detail;
            broadcastOpportunityChange(detail?.leadId);
            scheduleRefresh();
        };

        window.addEventListener('chat:message_received', handleNewMessage);
        window.addEventListener('chat:stage_changed', handleStageChanged);

        return () => {
            window.removeEventListener('chat:message_received', handleNewMessage);
            window.removeEventListener('chat:stage_changed', handleStageChanged);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [enabled, scheduleRefresh, broadcastOpportunityChange]);

    useEffect(() => {
        if (!enabled) return;

        const onAssignment = (event: Event) => {
            broadcastMeta((event as CustomEvent<MetaDetail>).detail, broadcastConversationChange);
        };
        const onMeta = (event: Event) => {
            broadcastMeta((event as CustomEvent<MetaDetail>).detail, broadcastConversationChange);
        };
        const onFavorite = (event: Event) => {
            const detail = (event as CustomEvent<FavoriteDetail>).detail;
            if (!detail?.conversationId || !detail.action) return;
            if (detail.source === 'partykit' || detail.source === 'broadcast') return;
            broadcastFavoriteChange(detail.conversationId, detail.action, detail.userId);
        };

        window.addEventListener('assignment:changed', onAssignment);
        window.addEventListener('conversation:meta_changed', onMeta);
        window.addEventListener('favorite:changed', onFavorite);
        return () => {
            window.removeEventListener('assignment:changed', onAssignment);
            window.removeEventListener('conversation:meta_changed', onMeta);
            window.removeEventListener('favorite:changed', onFavorite);
        };
    }, [enabled, broadcastConversationChange, broadcastFavoriteChange]);
}
